// ============================================================================
// BID PROJECT HOME
// Story #174: project-level document upload for the Bid Response module,
// split into two distinct classes — tender documents (feed BidRequirement
// extraction) and an evidence/case-study library (client, date, quantified
// outcomes, sourced status). See .claude/plans/bid-response-feature-plan.md
// "Project-level doc upload" for why these are kept separate.
// Story #175 adds a minimal extraction trigger + requirements panel for
// tender documents (just enough to run extraction and clear the numeric-field
// review gate). Story #176 adds the full cross-document compliance matrix
// (ComplianceMatrixView) alongside it.
// ============================================================================

import React, { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Library, Upload, Trash2, Loader2, Sparkles, ChevronDown, ChevronUp, AlertTriangle, Check, GitBranch, GitCompare } from 'lucide-react';
import {
  useBidDocuments,
  useUploadBidDocument,
  useDeleteBidDocument,
  useTriggerExtraction,
  useBidRequirements,
  useReviewRequirement
} from '../../hooks/useBid';
import { BidDocument, BidDocumentClass, BidEvidenceMetadata } from '../../services/bidService';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DiffPanel } from '../../components/bid/DiffPanel';
import { ComplianceMatrixView } from '../../components/bid/ComplianceMatrixView';

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}

function formatSourceRef(sourceRef: { page?: number; section?: string; sheetName?: string; rowIndex?: number }): string | null {
  if (sourceRef.page !== undefined) return `page ${sourceRef.page}`;
  if (sourceRef.section) return `§ ${sourceRef.section}`;
  if (sourceRef.sheetName !== undefined && sourceRef.rowIndex !== undefined) return `${sourceRef.sheetName} row ${sourceRef.rowIndex + 1}`;
  return null;
}

interface RequirementsPanelProps {
  projectId: string;
  documentId: string;
}

function RequirementsPanel({ projectId, documentId }: RequirementsPanelProps) {
  const { data: requirements, isLoading } = useBidRequirements(projectId, documentId);
  const reviewMutation = useReviewRequirement(projectId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-500 text-xs bg-slate-50/50">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading requirements...
      </div>
    );
  }

  if (!requirements?.length) {
    return <div className="py-6 text-center text-xs text-slate-400 bg-slate-50/50">No requirements extracted yet.</div>;
  }

  return (
    <div className="bg-slate-50/50 divide-y divide-[#e2e8f0]">
      {requirements.map(req => {
        const needsReview = req.reviewStatus === 'pending_review' || req.openIssues.length > 0;
        const ref = formatSourceRef(req.sourceRef);
        return (
          <div key={req.requirementId} className="px-6 py-3 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-navy">
                {req.clauseRef && <span className="text-slate-400 mr-1.5">{req.clauseRef}</span>}
                {req.text.length > 140 ? `${req.text.slice(0, 140)}…` : req.text}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                {req.category && <>{req.category} · </>}
                {req.weight !== undefined && <>weight {req.weight}% · </>}
                {req.deadline && <>due {req.deadline} · </>}
                {ref && <>{ref}</>}
              </p>
              {req.openIssues.length > 0 && (
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {req.openIssues.map((issue, i) => (
                    <li key={i} className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                      <AlertTriangle size={10} /> {issue}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {needsReview && (
              <button
                onClick={() => reviewMutation.mutate({ requirementId: req.requirementId, reviewStatus: 'reviewed', openIssues: [] })}
                disabled={reviewMutation.isPending}
                className="flex-shrink-0 flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-primary-dark border border-primary/30 hover:bg-primary/5 rounded-lg px-2.5 py-1 transition-colors"
              >
                <Check size={12} /> Mark reviewed
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface DocumentRowProps {
  doc: BidDocument;
  predecessorFileName?: string;
  onDelete: (documentId: string, fileName: string) => void;
  deleteIsPending: boolean;
  projectId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  isDiffExpanded: boolean;
  onToggleDiffExpand: () => void;
}

function DocumentRow({
  doc, predecessorFileName, onDelete, deleteIsPending, projectId,
  isExpanded, onToggleExpand, isDiffExpanded, onToggleDiffExpand
}: DocumentRowProps) {
  const extractMutation = useTriggerExtraction(projectId);
  const isTender = doc.docClass === 'tender';
  const isProcessing = doc.processingStatus === 'processing' || extractMutation.isPending;
  const isAddendum = isTender && !!doc.supersedesDocumentId;

  return (
    <div>
      <div className="flex items-center justify-between px-6 py-4 hover:bg-slate-50/50 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center size-9 bg-slate-50 text-slate-500 rounded-lg border border-slate-200 flex-shrink-0">
            <span className="text-[10px] font-black uppercase">{getFileExtension(doc.fileName)}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy truncate flex items-center gap-2">
              {doc.fileName}
              {isAddendum && (
                <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">
                  <GitBranch size={10} /> Addendum{predecessorFileName ? ` of ${predecessorFileName}` : ''}
                </span>
              )}
            </p>
            <p className="text-xs text-slate-500">
              {formatDate(doc.uploadedAt)} · {formatFileSize(doc.fileSizeBytes)}
              {doc.evidence && (
                <>
                  {doc.evidence.client && <> · {doc.evidence.client}</>}
                  {' · '}
                  <span className={doc.evidence.sourcedStatus === 'sourced' ? 'text-green-600' : 'text-amber-600'}>
                    {doc.evidence.sourcedStatus === 'sourced' ? 'Sourced' : 'Unsourced'}
                  </span>
                </>
              )}
              {isTender && doc.processingStatus && (
                <>
                  {' · '}
                  <span className={
                    doc.processingStatus === 'completed' ? 'text-green-600'
                      : doc.processingStatus === 'failed' ? 'text-red-600'
                        : 'text-slate-400'
                  }>
                    {doc.processingStatus === 'completed' ? 'Extracted'
                      : doc.processingStatus === 'failed' ? 'Extraction failed'
                        : doc.processingStatus === 'processing' ? 'Extracting…'
                          : 'Not yet extracted'}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isTender && (
            <>
              <button
                onClick={() => extractMutation.mutate(doc.documentId)}
                disabled={isProcessing}
                className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark border border-primary/30 hover:bg-primary/5 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
                title="Extract requirements"
              >
                {isProcessing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                Extract
              </button>
              {isAddendum && (
                <button
                  onClick={onToggleDiffExpand}
                  className={`flex items-center gap-1.5 text-xs font-semibold border rounded-lg px-3 py-1.5 transition-colors ${
                    isDiffExpanded ? 'text-primary border-primary/30 bg-primary/5' : 'text-slate-500 border-slate-200 hover:bg-slate-50'
                  }`}
                  title={isDiffExpanded ? 'Hide diff' : 'Show diff vs prior version'}
                >
                  <GitCompare size={13} /> Diff
                </button>
              )}
              <button
                onClick={onToggleExpand}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-lg hover:bg-slate-100"
                title={isExpanded ? 'Hide requirements' : 'Show requirements'}
              >
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </>
          )}
          <button
            onClick={() => onDelete(doc.documentId, doc.fileName)}
            disabled={deleteIsPending}
            className="text-slate-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
            title="Delete document"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      {isTender && isDiffExpanded && (
        <DiffPanel projectId={projectId} documentId={doc.documentId} predecessorFileName={predecessorFileName} />
      )}
      {isTender && isExpanded && <RequirementsPanel projectId={projectId} documentId={doc.documentId} />}
    </div>
  );
}

interface DocumentSectionProps {
  projectId: string;
  docClass: BidDocumentClass;
  title: string;
  description: string;
  icon: React.ElementType;
}

function DocumentSection({ projectId, docClass, title, description, icon: Icon }: DocumentSectionProps) {
  const { data: documents, isLoading, refetch } = useBidDocuments(projectId, docClass);
  const uploadMutation = useUploadBidDocument(projectId);
  const deleteMutation = useDeleteBidDocument(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ documentId: string; fileName: string } | null>(null);
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);
  const [diffExpandedDocumentId, setDiffExpandedDocumentId] = useState<string | null>(null);

  const [client, setClient] = useState('');
  const [date, setDate] = useState('');
  const [quantifiedOutcomes, setQuantifiedOutcomes] = useState('');
  const [sourcedStatus, setSourcedStatus] = useState<'sourced' | 'unsourced'>('unsourced');
  const [supersedesDocumentId, setSupersedesDocumentId] = useState('');

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    setIsUploading(true);
    try {
      const evidence: BidEvidenceMetadata | undefined = docClass === 'evidence'
        ? { client: client || undefined, date: date || undefined, quantifiedOutcomes: quantifiedOutcomes || undefined, sourcedStatus }
        : undefined;
      await uploadMutation.mutateAsync({
        file, docClass, evidence,
        supersedesDocumentId: docClass === 'tender' && supersedesDocumentId ? supersedesDocumentId : undefined
      });
      refetch();
      if (docClass === 'evidence') {
        setClient(''); setDate(''); setQuantifiedOutcomes(''); setSourcedStatus('unsourced');
      }
      if (docClass === 'tender') setSupersedesDocumentId('');
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [docClass, client, date, quantifiedOutcomes, sourcedStatus, supersedesDocumentId, uploadMutation, refetch]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFile(files[0]);
  }, [handleFile]);

  const handleDelete = (documentId: string, fileName: string) => {
    setPendingDelete({ documentId, fileName });
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.documentId, {
      onSettled: () => setPendingDelete(null)
    });
  };

  const dropZoneClass = isDragging
    ? 'border-primary bg-primary/5'
    : isUploading
      ? 'border-green-400 bg-green-50'
      : 'border-[#e2e8f0] bg-white hover:border-primary/50';

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Icon size={16} />
        </div>
        <div>
          <h2 className="text-sm font-bold text-navy">{title}</h2>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>

      <div className="p-5 border-b border-[#e2e8f0]">
        {docClass === 'tender' && !!documents?.length && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">
              This is an addendum to… <span className="font-normal text-slate-400">(optional — links it to a prior tender document for diffing)</span>
            </label>
            <select
              value={supersedesDocumentId}
              onChange={e => setSupersedesDocumentId(e.target.value)}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
            >
              <option value="">Not an addendum — new tender document</option>
              {documents.map(d => (
                <option key={d.documentId} value={d.documentId}>{d.fileName}</option>
              ))}
            </select>
          </div>
        )}
        {docClass === 'evidence' && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input
              value={client}
              onChange={e => setClient(e.target.value)}
              placeholder="Client"
              className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
            <input
              value={quantifiedOutcomes}
              onChange={e => setQuantifiedOutcomes(e.target.value)}
              placeholder="Quantified outcomes (e.g. 30% reduction in incident response time)"
              className="col-span-2 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
            <div className="col-span-2 flex rounded-xl border border-[#e2e8f0] overflow-hidden w-fit">
              {(['sourced', 'unsourced'] as const).map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSourcedStatus(opt)}
                  className={`px-4 py-2 text-xs font-semibold transition-all ${
                    sourcedStatus === opt ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {opt === 'sourced' ? 'Sourced' : 'Unsourced'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={e => { e.preventDefault(); setIsDragging(false); }}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${dropZoneClass}`}
        >
          {isUploading
            ? <Loader2 size={24} className="animate-spin text-primary" />
            : <Upload size={24} className="text-slate-400" />}
          <p className="text-sm text-slate-500">
            {isUploading ? 'Uploading…' : 'Drag & drop a file, or click to browse'}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading documents...
        </div>
      ) : !documents?.length ? (
        <div className="py-12 text-center">
          <Icon size={28} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">No documents yet.</p>
        </div>
      ) : (
        <div className="divide-y divide-[#e2e8f0]">
          {documents.map(doc => (
            <DocumentRow
              key={doc.documentId}
              doc={doc}
              predecessorFileName={documents.find(d => d.documentId === doc.supersedesDocumentId)?.fileName}
              onDelete={handleDelete}
              deleteIsPending={deleteMutation.isPending}
              projectId={projectId}
              isExpanded={expandedDocumentId === doc.documentId}
              onToggleExpand={() => setExpandedDocumentId(prev => prev === doc.documentId ? null : doc.documentId)}
              isDiffExpanded={diffExpandedDocumentId === doc.documentId}
              onToggleDiffExpand={() => setDiffExpandedDocumentId(prev => prev === doc.documentId ? null : doc.documentId)}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete document"
        message={pendingDelete ? `Are you sure you want to delete "${pendingDelete.fileName}"? This cannot be undone.` : ''}
        confirmText="Delete"
        variant="danger"
        isProcessing={deleteMutation.isPending}
      />
    </div>
  );
}

const BidProjectHome: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <FileText size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Bid Response</h1>
            <p className="text-slate-500 text-sm font-medium">Upload tender documents and build your evidence library</p>
          </div>
        </header>

        <DocumentSection
          projectId={projectId!}
          docClass="tender"
          title="Tender Documents"
          description="Statement of Requirements, Response Template, pricing schedule, addenda"
          icon={FileText}
        />

        <ComplianceMatrixView projectId={projectId!} />

        <DocumentSection
          projectId={projectId!}
          docClass="evidence"
          title="Evidence Library"
          description="Case studies with client, date, quantified outcomes and sourced status"
          icon={Library}
        />

      </div>
    </div>
  );
};

export default BidProjectHome;
